import { ODataResponse } from './types';

export interface StatusMapping {
  [key: string]: string[];
}

export interface NatureMapping {
  [key: string]: string;
}

export interface TypeMapping {
  [key: string]: string;
}

export interface DateFilterValue {
  startDate: Date | null;
  endDate: Date | null;
}

export interface StatusConfig {
  icon: 'error' | 'download' | 'refresh' | 'tick' | 'help';
  color: string;
  label: string;
  pillVariant: 'grey' | 'green' | 'red' | 'orange' | 'blue';
}

export const STATUS_GROUP_MAPPING: StatusMapping = {
  StatusReceipts: ['PartnerConformant'],
  StatusAccepted: ['AcceptedByDestination'],
  StatusInTreatment: [
    'TransformedByEdiPartner',
    'PostedToDestination',
    'AcknowledgedByDestination',
    'AuthorizedBySage',
    'InProgressWithAcceptance',
  ],
  StatusError: [
    'RejectedBySage',
    'TransformationFailed',
    'NonConformantEdi',
    'NonConformantFormat',
    'NonConformantData',
    'NonConformantVersion',
    'EdiRejectedByDestination',
    'EdiPartnerSendError',
    'CanceledByEdiPartner',
    'NonConformantAuthorization',
    'PartlyRejected',
    'InProgressWithErrors',
    'UnexpectedDeclaration',
    'SageSendError',
    'CommunicationWithDestinationFailed',
    'NonConformantXslTransformation',
    'RejectOrAnomaly',
  ],
};

export const NATURE_MAPPING: NatureMapping = {
  DsnMensuelle: 'Mensuelle',
  DsnAmorcage: 'Amorçage',
  DsnRepriseTravail: 'Reprise de travail',
  DsnFCTU: 'Fin de contrat unique',
  DsnFinContrat: 'Fin de contrat',
  DsnArretTravail: 'Arrêt de travail',
  DsnDpae: 'DPAE',
};

export const TYPE_MAPPING: TypeMapping = {
  Normale: 'Normale',
  NormaleNeant: 'Normale néant',
  AnnuleRemplace: 'Annule et remplace',
  Annule: 'Annule',
  AnnuleRempalceNeant: 'Annule et remplace néant',
};

export type StatusConfigKey =
  'StatusError' | 'StatusReceipts' | 'StatusInTreatment' | 'StatusAccepted';

export interface DsnDetailsData {
  declarant: {
    raisonSociale: string;
    siret: string;
  };
  declare: {
    raisonSociale: string;
    siret: string;
  };
  declaration: {
    numeroEnvoi: string;
    salarie?: string;
    nature: string;
    type: string;
    phase: string;
    modeEnvoi: boolean;
    periode: string;
    deposeLe: string;
    actualiseLe: string;
    statut: string;
  };
}

// ===== Types pour la réponse OData de la liste des DSNs =====

// Type pour les chemins relatifs de fichiers
export interface CheminRelatif {
  Location: string;
  Date: string;
}

// Type pour les tiers (Déclaré ou Déclarant)
export interface DsnTiers {
  InstanceId: string;
  Siret: string;
  RaisonSociale: string;
  TypeTiers: 'Redevable' | 'Declarant';
  LastModificationStamp: string;
}

// Type pour l'objet EnvoiSageDirect
export interface DsnEnvoiSageDirect {
  InstanceId: string;
  TypenameVfs: string;
  TypeTransformation: string;
  Application: string;
  ApplicationVersion: string;
  DateCreationInterchange: string;
  InstanceIdSageDirect: string;
  OpCoCode: string;
  ApplicationSerie: string;
  Status: string;
  InstanceIdEmetteur: string;
  TypeEnvoi: string;
  Test: boolean;
  LastModificationStamp: string;
  ApplicationSageDirectVersion: string | null;
  RefDossier: string | null;
  InstanceIdSoftware: string | null;
  NotificationsEmail: string;
  InstanceIdClientEc: string | null;
  InstanceIdUser: string;
  EnvoiImageStatus: string;
  ApplicationSerialNumber: string;
  FromAzure: boolean;
  CheckSum: number;
  Version: string;
  SiretDeclarant: string;
  PrenomDeclarant: string;
  Comment: string;
  NomDeclarant: string;
  CheminRelatifMessageRecu: CheminRelatif;
}

// Type principal pour une DSN individuelle dans le tableau value[]
export interface Dsn {
  InstanceId: string;
  TypenameVfs: string;
  TypeTransformation: string;
  LastModificationStamp: string;
  Status: string;
  SubState: string;
  InstanceIdEnvoiSageDirect: string;
  Contenu: string;
  OpCoCode: string;
  EnvoiEnDirect: boolean;
  DeclarationId: string;
  DateCreationInterchange: string;
  DateDebutPeriode: string;
  Test: boolean;
  Verifier: boolean;
  TypeDeclaration: string;
  Destinataire: string;
  DateFinPeriode: string;
  Surveiller: boolean;
  InstanceIdEnvoie: string;
  Replay: number;
  LastControlStamp: string | null;
  CountControl: number;
  SurveillerSysteme: boolean;
  TailleFichierEdi: number;
  InstanceIdDeclarant: string;
  ReferenceEnvoi: string;
  DestinatairesEmailLiberatoire: string | null;
  DateSupervision: string;
  DestinataireSupervision: string;
  NombrePaiements: string;
  CodeClientSupervision: string;
  Siret: string;
  CoolId: string;
  ReferenceJdc: string | null;
  InstanceIdJdcEnvoi: string | null;
  InstanceIdOption: string;
  LinkToContractDone: boolean;
  Periode: string;
  StatutClass: string;
  DateSuivi: string;
  Document: string;
  ContenuEx: string;
  RejectCode: string;
  RejectLabel: string | null;
  CodeTypeDeclaration: string;
  Nature: string;
  SalarieArretTravail: string;
  Etablissement: string;
  StatutInfos: string;
  InstanceIdDeclare: string;
  Fraction: string;
  Regime: string;
  DisplayPdfImg: boolean;
  DisplayZipImg: boolean;
  CheminRelatifFichierEdi: CheminRelatif;
  CheminRelatifTransform: CheminRelatif;
  DsnTiersDeclareObj: DsnTiers;
  DsnEnvoiSageDirectObj: DsnEnvoiSageDirect;
  DsnTiersDeclarantObj: DsnTiers;
}

// Type pour la réponse OData complète
export interface DsnListResponse extends ODataResponse<Dsn> {}

export interface DsnDetailsResponse extends ODataResponse<Dsn> {}
